
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to onboard users to Plaid Check with Layer.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title="Onboard users with Layer" subtitle="A guide to integrating Layer with Plaid Check" mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`This guide will walk you through how to onboard users to Plaid Check with Layer. This process involves creating a user, onboarding the user with Layer, and retrieving the user's Plaid Check data.`}</p>
    <h4 {...{
      "id": "integration-steps"
    }}>{`Integration steps`}</h4>
    <h5 {...{
      "id": "create-a-user"
    }}>{`Create a user`}</h5>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` endpoint. At this stage, the `}<inlineCode parentName="p">{`identity`}</inlineCode>{` field is optional.`}</p>
        <Callout mdxType="Callout">
          <p parentName="li">{`If you integrated with Consumer Report before December 2025, you have a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` rather than a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. Pass your stored `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` value wherever this guide refers to `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. See `}<a parentName="p" {...{
              "href": "/docs/api/users/migrate-to-new-user-apis/"
            }}>{`Migrate to new User APIs`}</a>{` for details.`}</p>
        </Callout>
      </li>
    </ol>
    <h5 {...{
      "id": "onboard-the-user-with-layer"
    }}>{`Onboard the user with Layer`}</h5>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Onboard the user by following Layer's `}<a parentName="p" {...{
            "href": "/docs/layer/#integration-overview"
          }}>{`Integration Overview steps`}</a>{`.`}<br />{` When creating the Link token via `}<inlineCode parentName="p">{`/session/token/create`}</inlineCode>{`, pass in the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` you created in the top-level `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` field and be sure the template used has CRA products enabled. `}</p>
        <Callout mdxType="Callout">
          <p parentName="li">{`If you are using Transactions in conjunction with both CRA products and Layer, then configure your Layer template with `}<inlineCode parentName="p">{`transactions`}</inlineCode>{` as an additional consented product (rather than a product) and call `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` only `}<em parentName="p">{`after`}</em>{` receiving the `}<inlineCode parentName="p">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook. This is necessary to avoid a known issue with Chase Bank Items in which CRA reports may fail to generate for Layer sessions if a Transactions extraction is triggered before the CRA report generation is complete.`}</p>
        </Callout>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/user_account/session/get`}</inlineCode>{` to retrieve user-permissioned identity information.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`After processing the identity information, update the Plaid user record by calling the `}<inlineCode parentName="p">{`/user/update`}</inlineCode>{` endpoint. Populate the `}<inlineCode parentName="p">{`identity`}</inlineCode>{` field with the relevant information. At minimum, the following fields must be provided and non-empty: `}<inlineCode parentName="p">{`name`}</inlineCode>{`, `}<inlineCode parentName="p">{`date_of_birth`}</inlineCode>{`, `}<inlineCode parentName="p">{`emails`}</inlineCode>{`, `}<inlineCode parentName="p">{`phone_numbers`}</inlineCode>{`, and `}<inlineCode parentName="p">{`addresses`}</inlineCode>{` (with at least one email address, phone number, and address designated as `}<inlineCode parentName="p">{`primary`}</inlineCode>{`). Providing at least a partial SSN via the `}<inlineCode parentName="p">{`id_numbers`}</inlineCode>{` field is highly recommended, since it improves the accuracy of matching user records during compliance processes such as file disclosure, dispute, or security freeze requests.`}</p>
      </li>
    </ol>
    <h5 {...{
      "id": "retrieve-the-users-plaid-check-data"
    }}>{`Retrieve the user's Plaid Check data`}</h5>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Now that the identity information has been added to the user record, you can generate a Consumer Report by calling `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the Consumer Report has been generated (indicated by a `}<inlineCode parentName="p">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook), retrieve the report by calling `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/cra/check_report/partner_insights/get`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/cra/check_report/pdf/get`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    <h5 {...{
      "id": "testing-in-sandbox"
    }}>{`Testing in Sandbox`}</h5>
    <p>{`Run the Layer portion of this flow against a `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profile`}</a>{`, using the OTP `}<inlineCode parentName="p">{`123456`}</inlineCode>{`; see `}<a parentName="p" {...{
        "href": "/docs/layer/#testing-layer-in-sandbox"
      }}>{`Testing Layer`}</a>{`. For the Consumer Report portion, see `}<a parentName="p" {...{
        "href": "/docs/check/add-to-app/#testing-in-sandbox"
      }}>{`Testing in Sandbox`}</a>{`. The `}<inlineCode parentName="p">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook can be triggered on demand with `}<inlineCode parentName="p">{`/sandbox/user/fire_webhook`}</inlineCode>{` rather than waiting for report generation.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-steps",
        level: 4,
        title: "Integration steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
